import React, { useState, useEffect, useCallback } from 'react';
import { Tv, Plus, Edit2, Trash2, Search, X, PlusCircle, Play } from 'lucide-react';
import { Card, Button, Input, Modal, Badge } from '../components/UI';
import Pagination from '../components/Pagination';
import { channelAPI } from '../api/apiClient';
import toast from 'react-hot-toast';

/**
 * Channel Manager Page
 */
const ChannelManagerPage = () => {
  const CHANNELS_PER_PAGE = 100;
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [streamTestModal, setStreamTestModal] = useState({ isOpen: false, source: null, channelName: '' });
  const [formData, setFormData] = useState({
    name: '',
    category: 'Movies',
    country: 'Global',
    description: '',
    sources: [{ name: 'Primary', url: '', priority: 1, isActive: true }],
    thumbnail: '',
  });

  const loadChannels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await channelAPI.getAll(page, CHANNELS_PER_PAGE, category, '', search);
      setChannels(response.data.data.channels);
      setTotalPages(response.data.data.pagination.pages);
      setTotalCount(response.data.data.pagination.total);
    } catch (error) {
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, [page, category, search]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const handleCreateChannel = async () => {
    try {
      if (!formData.name || !formData.sources.some(s => s.url.trim())) {
        toast.error('Please fill in channel name and at least one source URL');
        return;
      }

      // Validate all sources have URLs
      const invalidSources = formData.sources.filter(s => !s.url.trim());
      if (invalidSources.length > 0) {
        toast.error('All sources must have valid URLs');
        return;
      }

      await channelAPI.create(formData);
      toast.success('Channel created successfully');
      setIsModalOpen(false);
      resetForm();
      loadChannels();
    } catch (error) {
      toast.error('Failed to create channel');
    }
  };

  const handleUpdateChannel = async () => {
    try {
      await channelAPI.update(editingChannel._id, formData);
      toast.success('Channel updated successfully');
      setIsModalOpen(false);
      resetForm();
      loadChannels();
    } catch (error) {
      toast.error('Failed to update channel');
    }
  };

  const handleDeleteChannel = async (id) => {
    if (window.confirm('Are you sure you want to delete this channel?')) {
      try {
        await channelAPI.delete(id);
        toast.success('Channel deleted successfully');
        loadChannels();
      } catch (error) {
        toast.error('Failed to delete channel');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Movies',
      country: 'Global',
      description: '',
      sources: [{ name: 'Primary', url: '', priority: 1, isActive: true }],
      thumbnail: '',
    });
    setEditingChannel(null);
  };

  // Source management functions
  const addSource = () => {
    const newSource = {
      name: `Source ${formData.sources.length + 1}`,
      url: '',
      priority: formData.sources.length + 1,
      isActive: true,
    };
    setFormData({
      ...formData,
      sources: [...formData.sources, newSource],
    });
  };

  const updateSource = (index, field, value) => {
    const newSources = [...formData.sources];
    newSources[index] = { ...newSources[index], [field]: value };
    setFormData({ ...formData, sources: newSources });
  };

  const removeSource = (index) => {
    if (formData.sources.length <= 1) {
      toast.error('At least one source is required');
      return;
    }
    const newSources = formData.sources.filter((_, i) => i !== index);
    // Reorder priorities
    newSources.forEach((source, i) => {
      source.priority = i + 1;
    });
    setFormData({ ...formData, sources: newSources });
  };

  const moveSource = (index, direction) => {
    const newSources = [...formData.sources];
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= newSources.length) return;

    [newSources[index], newSources[newIndex]] = [newSources[newIndex], newSources[index]];

    // Update priorities
    newSources.forEach((source, i) => {
      source.priority = i + 1;
    });

    setFormData({ ...formData, sources: newSources });
  };

  const handleMoveChannel = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= channels.length) return;

    const updatedChannels = [...channels];
    [updatedChannels[index], updatedChannels[newIndex]] = [updatedChannels[newIndex], updatedChannels[index]];

    const previousChannels = [...channels];
    setChannels(updatedChannels);

    try {
      const updates = updatedChannels.map((channel, idx) => ({
        id: channel._id,
        priority: (page - 1) * CHANNELS_PER_PAGE + idx + 1,
      }));
      await channelAPI.bulkUpdate(updates);
      toast.success('Channel order updated');
    } catch (error) {
      setChannels(previousChannels);
      toast.error('Failed to update channel order');
    }
  };

  const testStream = (source, channelName) => {
    if (!source.url.trim()) {
      toast.error('No stream URL provided');
      return;
    }
    setStreamTestModal({ isOpen: true, source, channelName });
  };

  const closeStreamTest = () => {
    setStreamTestModal({ isOpen: false, source: null, channelName: '' });
  };

  const openEditModal = (channel) => {
    setEditingChannel(channel);
    setFormData(channel);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
            <Tv size={32} className="text-purple-400" />
            <span>Channel Manager</span>
          </h1>
          <p className="text-gray-400 mt-2">Manage IPTV channels and sources</p>
          <p className="text-sm text-gray-400 mt-2">
            Total channels: <span className="text-white">{totalCount.toLocaleString()}</span>
          </p>
        </div>
        <Button
          variant="primary"
          onClick={openCreateModal}
          className="mt-4 md:mt-0 flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Channel</span>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Search channels..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Categories</option>
            <option value="Movies">Movies</option>
            <option value="Sports">Sports</option>
            <option value="News">News</option>
            <option value="Entertainment">Entertainment</option>
            <option value="Kids">Kids</option>
            <option value="Music">Music</option>
          </select>
        </div>
      </Card>

      {/* Channels Table */}
      <Card>
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : channels.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No channels found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold w-12">
                    #
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold w-24">
                    Thumb
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Name
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Category
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Country
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Views
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Status
                  </th>
                  <th className="text-right px-6 py-3 text-gray-300 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {channels.map((channel, index) => (
                  <tr
                    key={channel._id}
                    className="border-b border-gray-700 hover:bg-gray-700 bg-opacity-50 transition"
                  >
                    <td className="px-6 py-3 text-gray-300">{(page - 1) * CHANNELS_PER_PAGE + index + 1}</td>
                    <td className="px-6 py-3">
                      <img
                        src={channel.thumbnail || 'https://via.placeholder.com/80x50?text=No+Image'}
                        alt={channel.name}
                        className="w-20 h-12 object-cover rounded-md border border-gray-600"
                      />
                    </td>
                    <td className="px-6 py-3 text-white font-medium">
                      {channel.name}
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant="info">{channel.category}</Badge>
                    </td>
                    <td className="px-6 py-3 text-gray-400">{channel.country}</td>
                    <td className="px-6 py-3 text-gray-400">
                      {channel.views.toLocaleString()}
                    </td>
                    <td className="px-6 py-3">
                      <Badge
                        variant={
                          channel.status === 'active' ? 'success' : 'danger'
                        }
                      >
                        {channel.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-right space-x-1 flex justify-end items-center">
                      <button
                        onClick={() => handleMoveChannel(index, -1)}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handleMoveChannel(index, 1)}
                        disabled={index === channels.length - 1}
                        className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => openEditModal(channel)}
                        className="text-blue-400 hover:text-blue-300 transition"
                        title="Edit channel"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteChannel(channel._id)}
                        className="text-red-400 hover:text-red-300 transition"
                        title="Delete channel"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(newPage) => {
            if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
          }}
        />
      </Card>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingChannel ? 'Edit Channel' : 'Add New Channel'}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={
                editingChannel ? handleUpdateChannel : handleCreateChannel
              }
            >
              {editingChannel ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className="space-y-4 max-h-96 overflow-y-auto">
          <Input
            label="Channel Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Sports Premium"
          />

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option>Movies</option>
              <option>Sports</option>
              <option>News</option>
              <option>Entertainment</option>
              <option>Kids</option>
              <option>Music</option>
            </select>
          </div>

          <Input
            label="Country"
            value={formData.country}
            onChange={(e) =>
              setFormData({ ...formData, country: e.target.value })
            }
            placeholder="e.g., US, UK, Global"
          />

          <Input
            label="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Channel description"
          />

          <Input
            label="Thumbnail URL"
            value={formData.thumbnail}
            onChange={(e) =>
              setFormData({ ...formData, thumbnail: e.target.value })
            }
            placeholder="https://example.com/thumbnail.jpg"
          />

          {/* Sources Management */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-300">
                Stream Sources
              </label>
              <Button
                variant="secondary"
                size="sm"
                onClick={addSource}
                className="flex items-center space-x-1"
              >
                <PlusCircle size={16} />
                <span>Add Source</span>
              </Button>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {formData.sources.map((source, index) => (
                <div
                  key={index}
                  className="bg-gray-700 rounded-lg p-4 border border-gray-600"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-300">
                        Source {index + 1}
                      </span>
                      <Badge variant={source.isActive ? 'success' : 'danger'}>
                        {source.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => moveSource(index, -1)}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveSource(index, 1)}
                        disabled={index === formData.sources.length - 1}
                        className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => testStream(source, formData.name || 'New Channel')}
                        className="text-green-400 hover:text-green-300"
                        title="Test stream"
                      >
                        <Play size={16} />
                      </button>
                      <button
                        onClick={() => removeSource(index)}
                        className="text-red-400 hover:text-red-300"
                        title="Remove source"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Source Name
                      </label>
                      <input
                        type="text"
                        value={source.name}
                        onChange={(e) => updateSource(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                        placeholder="e.g., Primary, Backup 1"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Priority
                      </label>
                      <input
                        type="number"
                        value={source.priority}
                        onChange={(e) => updateSource(index, 'priority', parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-400 mb-1">
                      Stream URL
                    </label>
                    <input
                      type="url"
                      value={source.url}
                      onChange={(e) => updateSource(index, 'url', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                      placeholder="http://stream.example.com/channel"
                      required
                    />
                  </div>

                  <div className="mt-3 flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`active-${index}`}
                      checked={source.isActive}
                      onChange={(e) => updateSource(index, 'isActive', e.target.checked)}
                      className="rounded border-gray-500 text-purple-600 focus:ring-purple-500"
                    />
                    <label
                      htmlFor={`active-${index}`}
                      className="text-sm text-gray-300 cursor-pointer"
                    >
                      Active
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {formData.sources.length === 0 && (
              <div className="text-center py-4 text-gray-400">
                No sources added. Click "Add Source" to add your first stream source.
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Stream Test Modal */}
      <Modal
        isOpen={streamTestModal.isOpen}
        onClose={closeStreamTest}
        title={`Test Stream - ${streamTestModal.channelName || 'Channel'}`}
      >
        <div className="space-y-4">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {streamTestModal.source?.name || 'Stream Source'}
                </h3>
                <p className="text-sm text-gray-400 truncate">
                  {streamTestModal.source?.url}
                </p>
              </div>
              <Badge variant={streamTestModal.source?.isActive ? 'success' : 'danger'}>
                {streamTestModal.source?.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            <div className="relative bg-black rounded-lg overflow-hidden">
              {streamTestModal.source?.url ? (
                <video
                  key={streamTestModal.source.url} // Force re-render when URL changes
                  controls
                  autoPlay
                  muted
                  className="w-full h-64 object-contain"
                  onError={(e) => {
                    toast.error('Stream failed to load. Check URL format.');
                  }}
                  onLoadStart={() => {
                    toast.loading('Loading stream...', { id: 'stream-test' });
                  }}
                  onCanPlay={() => {
                    toast.success('Stream loaded successfully!', { id: 'stream-test' });
                  }}
                >
                  <source src={streamTestModal.source.url} type="application/x-mpegURL" />
                  <source src={streamTestModal.source.url} type="video/mp4" />
                  <source src={streamTestModal.source.url} type="video/avi" />
                  <source src={streamTestModal.source.url} type="video/mkv" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="w-full h-64 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <Play size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No stream URL provided</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Priority:</span>
                <span className="text-white ml-2">{streamTestModal.source?.priority}</span>
              </div>
              <div>
                <span className="text-gray-400">Status:</span>
                <span className={`ml-2 ${streamTestModal.source?.isActive ? 'text-green-400' : 'text-red-400'}`}>
                  {streamTestModal.source?.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="secondary" onClick={closeStreamTest}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChannelManagerPage;
